import type { Theme } from '@mui/material/styles';

/**
 * Default prop and style configuration for the ELEVATOR UI theme.
 *
 * This centralizes global component defaults and style overrides so that
 * applications using the design system get consistent behaviour out of the box.
 *
 * More Info: https://mui.com/material-ui/customization/theme-components/#default-props
 */
const defaultPropsConfig = {
  components: {
    MuiCssBaseline: {
      /**
       * Ensures the document body uses the theme's background and text colors.
       */
      styleOverrides: (theme: Theme) => {
        const cssVars = (
          theme as Theme & {
            vars?: {
              palette: {
                background: { default: string };
                text: { primary: string };
              };
            };
          }
        ).vars;

        return {
          body: {
            backgroundColor:
              cssVars?.palette.background.default ?? theme.palette.background.default,
            color: cssVars?.palette.text.primary ?? theme.palette.text.primary,
          },
        };
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: false,
      },
      styleOverrides: {
        /**
         * Lighten primary contained buttons on hover instead of darkening.
         */
        containedPrimary: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.primary.light, // Lighten on hover instead of darken
          },
        }),
        /**
         * Lighten secondary contained buttons on hover instead of darkening.
         */
        containedSecondary: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.secondary.light, // Lighten on hover instead of darken
          },
        }),
        /**
         * Lighten error contained buttons on hover for clearer feedback.
         */
        containedError: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.error.light,
          },
        }),
        /**
         * Lighten warning contained buttons on hover.
         */
        containedWarning: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.warning.light,
          },
        }),
        /**
         * Lighten info contained buttons on hover.
         */
        containedInfo: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.info.light,
          },
        }),
        /**
         * Lighten success contained buttons on hover.
         */
        containedSuccess: ({ theme }: { theme: Theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.success.light,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        // Remove shrink: true to allow automatic floating label behavior
        // Labels will center when empty and shrink when focused/filled
      },
      styleOverrides: {
        root: {
          // Add border radius to all corners of filled TextField inputs
          '& .MuiFilledInput-root': {
            borderRadius: '4px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          // Add border radius to filled Select inputs to match TextField
          '& .MuiFilledInput-root': {
            borderRadius: '4px',
            '&::before, &::after': {
              borderRadius: '4px',
            },
          },
        },
        select: ({ theme }: { theme: Theme }) => ({
          // Match TextField padding for consistent height and spacing
          // This ensures Select inputs have the same visual appearance as TextField inputs
          paddingTop: theme.spacing(2.75), // 22px (matches TextField)
          paddingBottom: theme.spacing(0.25), // 2px (matches TextField)
          // Standardize font styling to match TextField
          fontFamily: 'Open Sans, sans-serif',
          fontWeight: 500,
          fontSize: '1.10rem',
          color: theme.palette.text.primary,
          '&:focus': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          // Ensure border radius on all corners for filled inputs
          borderRadius: '4px',
          '&::before, &::after': {
            borderRadius: '4px',
          },
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        select: ({ theme }: { theme: Theme }) => ({
          // Match TextField padding for consistent height and spacing
          // This ensures Select inputs have the same visual appearance as TextField inputs
          // Only targets NativeSelect (used by Select component with filled variant)
          paddingTop: theme.spacing(2.75), // 22px (matches TextField)
          paddingBottom: theme.spacing(0.25), // 2px (matches TextField)
          // Standardize font styling to match TextField
          fontFamily: 'Open Sans, sans-serif',
          fontWeight: 500,
          fontSize: '1.10rem',
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          // Standardize label color for filled variant inputs (TextField and Select)
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        square: true,
      },
      styleOverrides: {
        root: {
          boxShadow: 'none',
          // Do not set backgroundImage so elevation overlay works in dark mode
          // (MUI applies a semi-transparent white overlay via var(--Paper-overlay)).
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          alignItems: 'center' as const,
          flexDirection: 'row' as const,
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          display: 'flex' as const,
        },
      },
    },
  },
};

export default defaultPropsConfig;
